import { TranslateService } from '@ngx-translate/core';
import { AdditionalOrderInformationTemplate } from '../../../modules/core/models/additional-order-information-template.model';
import { FormGroup, FormBuilder, FormControl, FormArray, Validators } from '@angular/forms';
import { AdditionalOrderInformationType } from '../../../modules/core/enums/additional-order-information-type';
import { Option } from '../../../modules/core/models/option.model';
import * as _ from 'lodash';

export class AdditionalOrderInformationViewModel {
	collapsed: Boolean;
	formGroup: FormGroup;
	possibleValueMoving = false;
	originalFormGroupValue: any;
	dropDownOptions: Option[];
	isNew = false;
	clonedTemplate: AdditionalOrderInformationTemplate;

	constructor(
		public formBuilder: FormBuilder,
		public translate: TranslateService,
		public template: AdditionalOrderInformationTemplate,
		collapsed?: Boolean,
		previewMode?: Boolean
	) {
		this.init(template, collapsed, true, previewMode);
	}

	/**
	 * Set type of AdditionalOrderInformationTemplate
	 */
	public setAoitType(type: string) {
		this.template.type = AdditionalOrderInformationType[type];
		switch (this.template.type) {
			case AdditionalOrderInformationType.YES_NO:
				this.template.possibleValues = '';
				this.template.defaultValue = '';
				break;
			case AdditionalOrderInformationType.TEXT:
				this.template.possibleValues = '1';
				this.template.defaultValue = '';
				break;
			case AdditionalOrderInformationType.DROPDOWN:
				this.template.possibleValues = this.translate.instant('editAdditionalOrderInformation.card.placeholder.possibleValues');
				this.template.defaultValue = this.translate.instant('editAdditionalOrderInformation.card.placeholder.defaultValue');
				break;
		}
		return this.template;
	}

	/**
	 * Clone template
	 */
	public cloneTemplate() {
		this.clonedTemplate = _.clone(this.template);
	}

	/**
	 * Init additional order information view model
	 */
	init(template: AdditionalOrderInformationTemplate, collapsed: Boolean = true, disabled: Boolean = true, previewMode: Boolean = false) {
		this.template = template;
		this.collapsed = collapsed;
		this.dropDownOptions = [];

		const yesCheckbox = this.getCheckboxValue(template);
		const noCheckbox = this.getCheckboxValue(template, true);

		let possibleValues: any = template.possibleValues;

		if (template.type === AdditionalOrderInformationType.DROPDOWN) {
			if (possibleValues) {
				possibleValues = possibleValues.split(';');
				for (let i = 0; i < possibleValues.length; i++) {
					possibleValues[i] = possibleValues[i].trim();
				}
			} else {
				possibleValues = [];
			}
		}

		let possibleValuesCtrl = null;
		let isDefaultValueCtrl = null;
		if (Array.isArray(possibleValues)) {
			possibleValuesCtrl = this.formBuilder.array(possibleValues);

			const isDefaultValueArray: Boolean[] = [];
			possibleValues.forEach((value, idx) => {
				const isDefaultValue = value === template.defaultValue;
				isDefaultValueArray.push(isDefaultValue);
				this.dropDownOptions.push({ id: value, label: value });
			});
			isDefaultValueCtrl = this.formBuilder.array(isDefaultValueArray);

			isDefaultValueCtrl.controls.forEach((ctrl) => {
				this.addIsDefaultCtrlSubscription(ctrl, isDefaultValueCtrl, possibleValuesCtrl);
			});
		} else {
			possibleValuesCtrl = this.formBuilder.control(possibleValues, [Validators.pattern('^[0-9]*$'), Validators.min(1)]);
			isDefaultValueCtrl = this.formBuilder.control(false);
			this.dropDownOptions.push({ id: possibleValues, label: possibleValues });
		}

		// New form group
		this.formGroup = this.formBuilder.group({
			type: [template.type],
			id: [template.id, Validators.required],
			name: [template.name, [Validators.required]],
			possibleValues: possibleValuesCtrl,
			defaultValue: [template.defaultValue],
			isMandatory: [template.isMandatory],
			isActivated: [template.isActivated],
			yesCheckbox: [yesCheckbox],
			noCheckbox: [noCheckbox],
			isDefaultValue: isDefaultValueCtrl,
			previewValue: [template.defaultValue]
		});

		if (previewMode && template.isMandatory) {
			this.formGroup.controls['previewValue'].setValidators([Validators.required]);
		}

		if (disabled) {
			this.formGroup.disable();
		}

		this.originalFormGroupValue = Object.assign({}, this.formGroup.value);

		this.formGroup.get('yesCheckbox').valueChanges.subscribe((value) => {
			this.toggleYesNoCheckbox(value, 'yesCheckbox');
		});

		this.formGroup.get('noCheckbox').valueChanges.subscribe((value) => {
			this.toggleYesNoCheckbox(value, 'noCheckbox');
		});

		this.formGroup.get('isDefaultValue').valueChanges.subscribe((value) => {
			if (!this.possibleValueMoving && Array.isArray(value)) {
				let trueCount = 0;
				value.forEach((item) => {
					if (item === true) {
						trueCount++;
					}
				});

				if (trueCount !== 1) {
					// set empty default value
					this.formGroup.get('defaultValue').setValue(null, { onlySelf: true, emitEvent: false });
				}
			}
		});

		if (template.type === AdditionalOrderInformationType.DROPDOWN) {
			this.formGroup.get('possibleValues').valueChanges.subscribe((value) => {
				this.dropDownOptions = [];
				if (Array.isArray(value)) {
					this.updateNewDefaultValue(value, this.formGroup.get('defaultValue').value, this.formGroup.get('isDefaultValue').value);
					value.forEach((item) => {
						this.dropDownOptions.push({ id: item, label: item });
					});
				} else {
					this.formGroup.get('defaultValue').setValue(value, { onlySelf: true, emitEvent: false });
					this.dropDownOptions.push({ id: value, label: value });
				}
			});
		}

		this.formGroup.get('type').valueChanges.subscribe((newType) => {
			// Set new aoit type
			if (newType !== this.template.type) {
				this.setAoitType(newType);

				// Reinit view model
				this.init(this.template, this.collapsed, this.formGroup.disabled);
				// to enable save button after changing type of Aoit
				this.formGroup.markAsDirty({ onlySelf: true });
			}
		});
	}

	isYesNoType() {
		return this.template.type === AdditionalOrderInformationType.YES_NO;
	}

	toggleYesNoCheckbox(value: any, controlKey: string): any {
		let defaultValue = null;

		if (controlKey === 'yesCheckbox') {
			defaultValue = value === true ? 'yes' : null;
			this.formGroup.get('noCheckbox').setValue(null, { onlySelf: true, emitEvent: false });
		} else if (controlKey === 'noCheckbox') {
			defaultValue = value === true ? 'no' : null;
			this.formGroup.get('yesCheckbox').setValue(null, { onlySelf: true, emitEvent: false });
		}
		this.formGroup.get('defaultValue').setValue(defaultValue, { onlySelf: true, emitEvent: false });
	}

	possibleValueDown(idx: number) {
		this.possibleValueMoving = true;
		const isDefaultFormArray = <FormArray>this.formGroup.get('isDefaultValue');
		const possibleValuesFormArray = <FormArray>this.formGroup.get('possibleValues');

		const isDefaultCtrl = <FormControl>isDefaultFormArray.controls[idx];
		const possibleValueCtrl = <FormControl>possibleValuesFormArray.controls[idx];

		this.possibleValueRemove(idx);
		this.possibleValueAdd(idx, possibleValueCtrl, isDefaultCtrl);
		this.possibleValueMoving = false;
		this.formGroup.markAsDirty();
	}

	possibleValueUp(idx: number) {
		this.possibleValueMoving = true;
		const isDefaultFormArray = <FormArray>this.formGroup.get('isDefaultValue');
		const possibleValuesFormArray = <FormArray>this.formGroup.get('possibleValues');

		const isDefaultCtrl = <FormControl>isDefaultFormArray.controls[idx];
		const possibleValueCtrl = <FormControl>possibleValuesFormArray.controls[idx];

		this.possibleValueRemove(idx);
		this.possibleValueAdd(idx - 2, possibleValueCtrl, isDefaultCtrl);
		this.possibleValueMoving = false;
		this.formGroup.markAsDirty();
	}

	possibleValueRemove(idx: number) {
		const isDefaultFormArray = <FormArray>this.formGroup.get('isDefaultValue');
		const possibleValuesFormArray = <FormArray>this.formGroup.get('possibleValues');

		isDefaultFormArray.removeAt(idx);
		possibleValuesFormArray.removeAt(idx);
		this.formGroup.markAsDirty();
	}

	possibleValueAdd(idx: number, possibleValueCtrl?: FormControl, isDefaultCtrl?: FormControl) {
		const isDefaultFormArray = <FormArray>this.formGroup.get('isDefaultValue');
		const possibleValuesFormArray = <FormArray>this.formGroup.get('possibleValues');

		if (isDefaultCtrl == null) {
			isDefaultCtrl = new FormControl(false);
			this.addIsDefaultCtrlSubscription(isDefaultCtrl);
		}

		if (possibleValueCtrl == null) {
			possibleValueCtrl = new FormControl('');
		}

		isDefaultFormArray.insert(idx + 1, isDefaultCtrl);
		possibleValuesFormArray.insert(idx + 1, possibleValueCtrl);
		this.formGroup.markAsDirty();
	}

	onCancel() {
		this.formGroup.disable();
		this.formGroup.setValue(this.originalFormGroupValue);
		if (this.clonedTemplate) {
			this.template = Object.assign(this.template, this.clonedTemplate);
			this.init(this.template, this.collapsed, this.formGroup.disabled);
			this.clonedTemplate = null;
		}
	}

	getTemplate() {
		const value = this.formGroup.value;
		const template = new AdditionalOrderInformationTemplate();

		template.type = value.type;
		template.isActivated = value.isActivated;
		template.isMandatory = value.isMandatory;
		template.defaultValue = value.defaultValue;
		template.id = value.id;
		template.name = value.name;
		template.position = this.template.position;

		if (!this.isNew) {
			template.uid = this.template.uid;
		}

		if (Array.isArray(value.possibleValues)) {
			template.possibleValues = value.possibleValues.join(';');
		} else {
			template.possibleValues = value.possibleValues;
			if (
				template.type === 'TEXT' &&
				(template.possibleValues || (template.possibleValues as any) === 0) &&
				Number(template.possibleValues) < 1
			) {
				template.possibleValues = '1';
			}
		}

		return template;
	}

	private getCheckboxValue(template: AdditionalOrderInformationTemplate, invert: boolean = false) {
		let value: any = null;
		if (template.type === AdditionalOrderInformationType.YES_NO) {
			if (template.defaultValue === 'yes') {
				value = true;
			}
			if (template.defaultValue === 'no') {
				value = false;
			}
		}

		if (value != null && invert === true) {
			value = !value;
		}

		return value;
	}

	private addIsDefaultCtrlSubscription(ctrl: FormControl, isDefaultFormArray?: FormArray, possibleValuesFormArray?: FormArray) {
		if (isDefaultFormArray == null) {
			isDefaultFormArray = <FormArray>this.formGroup.get('isDefaultValue');
		}
		if (possibleValuesFormArray == null) {
			possibleValuesFormArray = <FormArray>this.formGroup.get('possibleValues');
		}

		ctrl.valueChanges.subscribe((value) => {
			if (value === true) {
				let curIdx = -1;

				// Disable other controls
				isDefaultFormArray.controls.forEach((item, index) => {
					if (item !== ctrl) {
						item.setValue(false, { onlySelf: true, emitEvent: false });
					} else {
						curIdx = index;
					}
				});

				// set new default value
				this.formGroup.get('defaultValue').setValue(possibleValuesFormArray.controls[curIdx].value, { onlySelf: true, emitEvent: false });
			}
		});
	}

	/**
	 * Updates the default Value if the possibleValues are changed
	 * Only Updates if possibleValue is set as DefaultValue
	 *
	 * @param newDropDownOptions
	 * @param currentDefaultValue
	 * @param newIsDefaultValues
	 */
	private updateNewDefaultValue(newDropDownOptions: string[], currentDefaultValue: string, newIsDefaultValues: boolean[]) {
		let i = 0;
		for (const option of newIsDefaultValues) {
			if (option) {
				// set new default value
				this.formGroup.get('defaultValue').setValue(newDropDownOptions[i], { onlySelf: true, emitEvent: false });
				break;
			}
			i++;
		}
	}
}
